import React, { useState, useCallback, useRef } from 'react';
import { PlaybookTransformItem, PlaybookEventConfig, PlaybookTransformType, ConnectionConfig, PlaybookParamValue } from '@/types/playbook';
import { EditorSelection } from './PlaybookEditorTab';
import { UnifiedSchema, EventSchema } from '@/types/schema';
import PlaybookTransformEditorPanelHeader from './PlaybookTransformEditorPanelHeader';
import PlaybookTransformList from './transforms-list/PlaybookTransformList';
import { toSnakeCase } from '../../utils/stringUtils';
import PlaybookAIMappingSection, { PlaybookAIMappingSectionRef } from './ai-mapping/PlaybookAIMappingSection';
import { getDefaultContent, isPropertyPairApplicableToAllEvents } from './PlaybookTransformUtils';
import ConnectionParametersForm from './ConnectionParametersForm';
import { Globe, Flag } from 'lucide-react';

import { PlaybookViewMode } from '@/components/playbooks/transforms-list/PlaybookTransformList';

interface PlaybookTransformEditorPanelProps {
  selection: EditorSelection;
  selectedEvent: PlaybookEventConfig | null;
  selectedTransform: PlaybookTransformItem | null;
  transforms: PlaybookTransformItem[];
  onAddTransforms: (transforms: PlaybookTransformItem[]) => void;
  onUpdateTransform: (transformId: string, updates: Partial<PlaybookTransformItem>) => void;
  onBatchUpdateTransforms?: (batchUpdates: { [transformId: string]: Partial<PlaybookTransformItem> }) => void;
  onDeleteTransform: (transformId: string) => void;
  onTransformSelect: (transformId: string) => void;
  onSelectionChange: (selection: EditorSelection) => void;
  onUpdateEvent: (eventId: string, updates: Partial<PlaybookEventConfig>) => void;
  inputSchema: UnifiedSchema | null;
  outputSchema: UnifiedSchema | null;
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[];
  allUiTransforms: PlaybookTransformItem[];
  allowUndefinedTransforms: boolean;
  baselineSchema?: 'input' | 'output';
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  viewMode: PlaybookViewMode;
  setViewMode: (mode: PlaybookViewMode) => void;
  connectionJSON: ConnectionConfig | null;
  connectionValues: PlaybookParamValue[]; // Add saved connection parameter values
  onUpdateConnection?: (connection: object) => void; // Add connection update handler
}

const PlaybookTransformEditorPanel: React.FC<PlaybookTransformEditorPanelProps> = ({
  selection,
  selectedEvent,
  selectedTransform,
  transforms,
  onAddTransforms,
  onUpdateTransform,
  onBatchUpdateTransforms,
  onDeleteTransform,
  onTransformSelect,
  onSelectionChange,
  onUpdateEvent,
  inputSchema,
  outputSchema,
  allEvents,
  uiEventConfigs,
  allUiTransforms,
  allowUndefinedTransforms,
  baselineSchema,
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode,
  connectionJSON, // Destructure from props
  connectionValues, // Destructure from props
  onUpdateConnection, // Destructure from props
}) => {
  // Event editing state
  const [isEventNameEditing, setIsEventNameEditing] = useState(false);
  const [isEventCodeNameEditing, setIsEventCodeNameEditing] = useState(false);
  const [isEventDescriptionEditing, setIsEventDescriptionEditing] = useState(false);
  const [eventCodeNameError, setEventCodeNameError] = useState<string | null>(null);

  // Refs for input focus management
  const eventNameInputRef = useRef<HTMLInputElement>(null);
  const eventCodeNameInputRef = useRef<HTMLInputElement>(null);
  const eventDescriptionInputRef = useRef<HTMLTextAreaElement>(null);
  const aiMappingSectionRef = useRef<PlaybookAIMappingSectionRef>(null);
  // Handle event field changes
  const handleEventChange = useCallback((eventId: string, field: 'name' | 'codeName' | 'description', value: string) => {
    const currentEvent = selectedEvent;
    if (!currentEvent) return;

    let updatedCodeName = currentEvent.codeName;
    let newEventUpdates: Partial<PlaybookEventConfig> = { [field]: value };

    if (field === 'name') {
      if (currentEvent.codeName === toSnakeCase(currentEvent.name)) {
        updatedCodeName = toSnakeCase(value);
        newEventUpdates.codeName = updatedCodeName;
      }
    }

    if (field === 'codeName' || (field === 'name' && newEventUpdates.codeName !== undefined)) {
      const codeNameToValidate = field === 'codeName' ? value : updatedCodeName;
      const isValid = /^[a-z0-9_]+$/.test(codeNameToValidate);
      if (!isValid && codeNameToValidate) {
        setEventCodeNameError('Code name must contain only lowercase letters, numbers, and underscores');
      } else {
        setEventCodeNameError(null);
      }
    }
    
    if (field !== 'codeName' || !eventCodeNameError) {
      onUpdateEvent(eventId, newEventUpdates);
    }
  }, [selectedEvent, onUpdateEvent, eventCodeNameError]);

  // Helper to get section title for the header
  const getSectionTitle = useCallback(() => {
    if (selection.type === 'global') {
      return 'Global Transforms';
    } else if (selection.type === 'event' && selectedEvent) {
      return selectedEvent.name || 'Event Transforms';
    } else if (selection.type === 'connection') {
      return 'Connection Settings';
    }
    return 'Transforms';
  }, [selection, selectedEvent]);

  // Handlers for new header functionality
  const handleAutoMap = useCallback(() => {
    console.log('Auto-map clicked');
    aiMappingSectionRef.current?.handleAutoMap();
  }, []);

  const handleAIMatch = useCallback(() => {
    console.log('AI matching clicked');
    aiMappingSectionRef.current?.handleAIMatch();
  }, []);

  const handleAddTransform = useCallback((type: PlaybookTransformType, currentSelection: EditorSelection) => {
    console.log('handleAddTransform called with type:', type);
    console.log('Current selection:', currentSelection);

    let appliesToScope: 'global' | 'eventSpecific';
    let eventIds: string[] | undefined;

    if (type === 'filter') {
      appliesToScope = 'global';
      eventIds = undefined; // Filters are global, so no specific eventIds
    } else {
      // For non-filter transforms (mapping, expression, enrichment, spreading)
      if (currentSelection.type === 'event' && currentSelection.eventId) {
        appliesToScope = 'eventSpecific';
        eventIds = [currentSelection.eventId];
      } else {
        // If not on an event section, or no event selected, add as global
        appliesToScope = 'global';
        eventIds = undefined;
      }
    }

    const newTransform: PlaybookTransformItem = {
      id: `transform_${Date.now()}`,
      type,
      content: getDefaultContent(type),
      appliesToScope,
      eventIds,
      isNew: true,
    };
    onAddTransforms([newTransform]);
  }, [onAddTransforms]);

  const totalTransformsCount = transforms.length; // Assuming 'transforms' is the array of transforms to count

  return (
    <>
      <div className="w-full flex flex-col flex-1">
        <PlaybookTransformEditorPanelHeader
          selection={selection}
          selectedEvent={selectedEvent}
          getSectionTitle={getSectionTitle}
          isEventNameEditing={isEventNameEditing}
          setIsEventNameEditing={setIsEventNameEditing}
          isEventCodeNameEditing={isEventCodeNameEditing}
          setIsEventCodeNameEditing={setIsEventCodeNameEditing}
          isEventDescriptionEditing={isEventDescriptionEditing}
          setIsEventDescriptionEditing={setIsEventDescriptionEditing}
          eventCodeNameError={eventCodeNameError}
          eventNameInputRef={eventNameInputRef}
          eventCodeNameInputRef={eventCodeNameInputRef}
          eventDescriptionInputRef={eventDescriptionInputRef}
          handleEventChange={handleEventChange}
          allowUndefinedTransforms={allowUndefinedTransforms}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          viewMode={viewMode}
          setViewMode={setViewMode}
          onAutoMap={handleAutoMap}
          onAIMatch={handleAIMatch}
          onAddTransform={handleAddTransform}
          inputSchema={inputSchema}
          outputSchema={outputSchema}
          totalTransformsCount={totalTransformsCount}
        />

        {/* Content based on selection */}
        <div className="flex-1 overflow-y-auto pb-10">
          {selection.type === 'connection' ? (
            <ConnectionParametersForm
              connectionJSON={connectionJSON ? {
                params: connectionJSON.params,
                values: connectionValues
              } : null}
              onUpdateConnectionValues={(values) => {
                console.log('Connection values updated:', values);
                
                // NOTE: Connection parameter VALUES (for playbook.yaml) are handled differently 
                // from connection parameter DEFINITIONS (for connection.yaml).
                // This form updates parameter VALUES which should be stored in the playbook state
                // as 'params' array, NOT in the connectionJSON which defines the parameter structure.
                
                // Update the playbook's parameter values (this updates playbook.yaml)
                // The connectionJSON contains parameter definitions and should not be modified here
                if (onUpdateConnection) {
                  // Instead of updating connectionJSON, we should update the playbook's params
                  // But since we don't have direct access to updateState here, we need to 
                  // pass the values up through a different mechanism
                  console.log('Connection parameter values should update playbook params, not connectionJSON');
                  // For now, we'll pass the values up and let the parent handle it correctly
                  onUpdateConnection({ parameterValues: values });
                } else {
                  console.log('onUpdateConnection not available');
                }
              }}
            />
          ) : (
            <div className="px-4">
              <PlaybookAIMappingSection
                ref={aiMappingSectionRef}
                inputSchema={inputSchema}
                outputSchema={outputSchema}
                transforms={transforms}
                onAddTransforms={onAddTransforms}
                onUpdateTransform={onUpdateTransform}
                onBatchUpdateTransforms={onBatchUpdateTransforms}
                baselineSchema={baselineSchema}
                selection={selection}
                allowUndefinedTransforms={allowUndefinedTransforms}
                allEvents={allEvents}
                allUiTransforms={allUiTransforms}
                isPropertyPairApplicableToAllEvents={(inputPath, outputPath) => isPropertyPairApplicableToAllEvents(inputPath, outputPath, allEvents)}
                uiEventConfigs={uiEventConfigs}
              />
              {transforms.length === 0 && (selection.type === 'global' || selection.type === 'event') ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  {selection.type === 'global' ? (
                    <Globe className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                  ) : (
                    <Flag className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                  )}
                  <div className="text-gray-500 dark:text-gray-400 font-medium mb-2">
                    No transforms configured
                  </div>
                  <div className="text-sm text-gray-400 dark:text-gray-500">
                    Add transforms to this playbook to see them here.
                  </div>
                </div>
              ) : (
                <PlaybookTransformList
                  transforms={transforms}
                  selectedTransform={selectedTransform}
                  onTransformSelect={onTransformSelect}
                  onDeleteTransform={onDeleteTransform}
                  onUpdateTransform={onUpdateTransform}
                  onSelectionChange={onSelectionChange}
                  inputSchema={inputSchema}
                  outputSchema={outputSchema}
                  allEvents={allEvents}
                  uiEventConfigs={uiEventConfigs}
                  allUiTransforms={allUiTransforms}
                  allowUndefinedTransforms={allowUndefinedTransforms}
                  searchTerm={searchTerm}
                  viewMode={viewMode}
                  selection={selection} // Pass selection prop
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PlaybookTransformEditorPanel;
